import React, { Component } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Helmet } from 'react-helmet';

class Reports extends Component {
  constructor(props) {
    super(props);
    this.state = { reports: [], filter: 'all', loading: true, error: '', userNames: {}, venueNames: {}, postTexts: {}, search: '', page: 1 };
    this.dismiss = this.dismiss.bind(this);
    this.removePost = this.removePost.bind(this);
    this.markActioned = this.markActioned.bind(this);
  }

  componentDidMount() {
    firebase.firestore().collection('reports').orderBy('createdAt', 'desc').limit(100)
      .get().then(snap => {
        const reports = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        this.setState({ reports, loading: false });
        this.resolveNames(reports);
      }).catch(() => this.setState({ loading: false }));
  }

  // Reports only store reporterUid and postId, so look up the reporter's name,
  // and the post's venue and that venue's name, for display.
  resolveNames(reports) {
    const db = firebase.firestore();
    const uids = [...new Set(reports.map(r => r.reporterUid).filter(Boolean))];
    const postIds = [...new Set(reports.map(r => r.postId).filter(Boolean))];

    Promise.all([
      Promise.all(uids.map(uid => db.collection('users').doc(uid).get()
        .then(d => [uid, d.exists ? (d.data().name || d.data().username || d.data().email || uid) : uid])
        .catch(() => [uid, uid]))),
      Promise.all(postIds.map(pid => db.collection('posts').doc(pid).get()
        .then(d => [pid, d.exists ? d.data() : null])
        .catch(() => [pid, null]))),
    ]).then(([userPairs, postPairs]) => {
      const userNames = Object.fromEntries(userPairs);
      const postText = Object.fromEntries(postPairs.map(([pid, data]) => [pid, data ? (data.text || '(no text)') : 'Post deleted']));
      const postVenue = Object.fromEntries(postPairs.map(([pid, data]) => [pid, data ? (data.venueId || null) : null]));
      const venueIds = [...new Set(Object.values(postVenue).filter(Boolean))];
      return Promise.all(venueIds.map(vid => db.collection('bar2').doc(vid).get()
        .then(d => [vid, d.exists ? (d.data().name || vid) : vid])
        .catch(() => [vid, vid]))).then(venuePairs => {
        const venueNames = Object.fromEntries(venuePairs);
        const venueByPost = Object.fromEntries(Object.entries(postVenue).map(([pid, vid]) => [pid, vid ? (venueNames[vid] || vid) : null]));
        this.setState({ userNames, venueNames: venueByPost, postTexts: postText });
      });
    }).catch(() => {});
  }

  dismiss(id) {
    firebase.firestore().collection('reports').doc(id).update({ status: 'dismissed' }).then(() => {
      this.setState(prev => ({
        reports: prev.reports.map(r => r.id === id ? { ...r, status: 'dismissed' } : r),
      }));
    });
  }

  removePost(id, postId) {
    const db = firebase.firestore();
    const batch = db.batch();
    if (postId) batch.update(db.collection('posts').doc(postId), { platformRemoved: true });
    batch.update(db.collection('reports').doc(id), { status: 'actioned' });
    batch.commit().then(() => {
      this.setState(prev => ({
        reports: prev.reports.map(r => r.id === id ? { ...r, status: 'actioned' } : r),
        error: '',
      }));
    }).catch(err => {
      this.setState({ error: err.message || 'Failed to remove post. Check Firestore rules.' });
    });
  }

  markActioned(id) {
    firebase.firestore().collection('reports').doc(id).update({ status: 'actioned' }).then(() => {
      this.setState(prev => ({
        reports: prev.reports.map(r => r.id === id ? { ...r, status: 'actioned' } : r),
      }));
    });
  }

  // Everything a row displays, so search matches what the admin sees
  rowText(r) {
    const { userNames, venueNames, postTexts } = this.state;
    return [
      userNames[r.reporterUid] || r.reporterEmail || r.reporterUid,
      r.reason,
      postTexts[r.postId] || r.postText,
      r.venueName || venueNames[r.postId] || r.venueId,
      r.status || 'pending',
    ].filter(Boolean).join(' ').toLowerCase();
  }

  render() {
    const { reports, filter, loading, search, page } = this.state;
    const FILTERS = ['all', 'pending', 'actioned', 'dismissed'];
    const PAGE_SIZE = 10;
    const q = search.trim().toLowerCase();
    const byStatus = filter === 'all' ? reports : reports.filter(r => (r.status || 'pending') === filter);
    const filtered = q ? byStatus.filter(r => this.rowText(r).includes(q)) : byStatus;
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);
    const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
      <div>
        <Helmet><title>Reports — BarZone</title></Helmet>
        <div style={{ padding: 20 }}>
          <h2 style={{ color: '#1a1a1a' }}>Reports</h2>
          <input
            type="search"
            placeholder="Search reporter, reason, post, venue…"
            value={search}
            onChange={e => this.setState({ search: e.target.value, page: 1 })}
            style={{ width: '100%', maxWidth: 420, padding: '8px 12px', marginBottom: 14, border: '1px solid #ddd', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', color: '#1a1a1a', background: '#fff' }}
          />
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {FILTERS.map(f => (
              <button key={f} onClick={() => this.setState({ filter: f, page: 1 })}
                style={{
                  padding: '6px 16px',
                  borderRadius: 20,
                  border: '1px solid #ddd',
                  background: filter === f ? '#FFD700' : '#fff',
                  fontWeight: filter === f ? 700 : 400,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  fontFamily: 'inherit',
                }}>
                {f}
              </button>
            ))}
          </div>
          {this.state.error && (
            <div style={{ background: '#fdecea', border: '1px solid #e74c3c', borderRadius: 4, padding: '10px 14px', marginBottom: 16, color: '#c0392b', fontSize: 13 }}>
              {this.state.error}
            </div>
          )}
          {loading ? <p>Loading...</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  {['Reporter', 'Reason', 'Post', 'Venue', 'Date', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontSize: 13 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(r => {
                  const status = r.status || 'pending';
                  const done = status !== 'pending';
                  const date = r.createdAt && r.createdAt.toDate ? r.createdAt.toDate().toLocaleDateString() : '';
                  const statusColor = status === 'pending' ? '#e67e22' : status === 'actioned' ? '#2ecc71' : '#95a5a6';
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #eee', opacity: done ? 0.7 : 1 }}>
                      <td style={{ padding: '10px 12px', fontSize: 13 }}>{this.state.userNames[r.reporterUid] || r.reporterEmail || r.reporterUid || '—'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13 }}>{r.reason || '—'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: '#666', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{this.state.postTexts[r.postId] || r.postText || 'Loading…'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13 }}>{r.venueName || this.state.venueNames[r.postId] || r.venueId || '—'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13 }}>{date}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: statusColor, color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 12, textTransform: 'capitalize' }}>{status}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            disabled={done}
                            onClick={() => this.dismiss(r.id)}
                            style={{ padding: '4px 10px', fontSize: 12, background: done ? '#eee' : '#fff', border: '1px solid #ddd', borderRadius: 4, cursor: done ? 'default' : 'pointer', fontFamily: 'inherit' }}
                          >
                            Dismiss
                          </button>
                          <button
                            disabled={done}
                            onClick={() => this.removePost(r.id, r.postId)}
                            style={{ padding: '4px 10px', fontSize: 12, background: done ? '#eee' : '#e74c3c', color: done ? '#888' : '#fff', border: 'none', borderRadius: 4, cursor: done ? 'default' : 'pointer', fontFamily: 'inherit' }}
                          >
                            Remove Post
                          </button>
                          <button
                            disabled={done}
                            onClick={() => this.markActioned(r.id)}
                            style={{ padding: '4px 10px', fontSize: 12, background: done ? '#eee' : '#2ecc71', color: done ? '#888' : '#fff', border: 'none', borderRadius: 4, cursor: done ? 'default' : 'pointer', fontFamily: 'inherit' }}
                          >
                            Mark Actioned
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && filtered.length === 0 && <p style={{ color: '#888', marginTop: 20 }}>No reports found.</p>}
          {!loading && filtered.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8, color: '#1a1a1a', fontSize: 13 }}>
              <span>
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button
                  disabled={currentPage <= 1}
                  onClick={() => this.setState({ page: currentPage - 1 })}
                  style={{ padding: '4px 10px', fontSize: 12, background: '#fff', border: '1px solid #ddd', borderRadius: 4, cursor: currentPage <= 1 ? 'default' : 'pointer', opacity: currentPage <= 1 ? 0.5 : 1, fontFamily: 'inherit' }}
                >
                  Prev
                </button>
                <span>Page {currentPage} of {pageCount}</span>
                <button
                  disabled={currentPage >= pageCount}
                  onClick={() => this.setState({ page: currentPage + 1 })}
                  style={{ padding: '4px 10px', fontSize: 12, background: '#fff', border: '1px solid #ddd', borderRadius: 4, cursor: currentPage >= pageCount ? 'default' : 'pointer', opacity: currentPage >= pageCount ? 0.5 : 1, fontFamily: 'inherit' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Reports;
